import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import i18next from 'i18next';
import * as Sentry from '@sentry/browser';

import { useAppStore as useAppStoreUntyped } from '@edf-pkg/app-main';

interface AppStore {
    initializeApp: () => () => void;
}
const useAppStore = useAppStoreUntyped as <T>(selector: (store: AppStore) => T) => T;

import Chat from '$app-web/components/chat';
import { CATEGORY_TYPES } from '$app-web/components/chat/constants';
import ChatProvider from '$app-web/components/chat/provider';
import { useMethods as useChatClientMethods, useVoip } from '$app-web/components/chat/state/chat-client.state';
import FullLoadingIndicator from '$app-web/components/full-loading-indicator';
import ClientProvider from '$app-web/core/client/provider';
import { initializeAppDateTime } from '$app-web/utils/app-date-time/utils';

import { buildStudyNameGetter, deriveChatFlags, StandaloneStudiesMap } from './derive-chat-flags';
import classes from './styles.module.scss';
import { CALL_VIEW_MODES, CallStateChangedPayload, CallViewChangedPayload } from './types';
import { useCallStateReporter } from './use-call-state-reporter';
import { useCallViewReporter } from './use-call-view-reporter';
import { CallCommandHandlers, useHandshake } from './use-handshake';
import { IncomingCallPayload, useIncomingCallReporter } from './use-incoming-call-reporter';
import { usePostMessageBridge } from './use-postmessage-bridge';
import useStudyUsersForChat from './use-study-users-for-chat';
import { useUnreadCountReporter } from './use-unread-count-reporter';

type SendToParent = ReturnType<typeof usePostMessageBridge>['sendToParent'];

const StandaloneChatPage = () => {
    const initializeApp = useAppStore((store) => store.initializeApp);

    useEffect(() => {
        // dayjs plugins (relativeTime, etc.) — needed for chat's RoomsList `.toNow()` calls.
        // The i18n saga normally does this, but it's gated on a space handshake the standalone
        // space doesn't perform, so we initialize directly here.
        initializeAppDateTime();

        const unsubscribe = initializeApp();
        return () => {
            if (typeof unsubscribe === 'function') unsubscribe();
        };
    }, [initializeApp]);

    const { status, readyState, sendToParent, callCommandHandlersRef } = useHandshake();

    if (status === 'timed-out') {
        return <div className={classes.error}>{i18next.t('generic_error_msg')}</div>;
    }

    if (status === 'loading' || readyState === null) {
        return <FullLoadingIndicator />;
    }

    return (
        <div className={classes.root}>
            <ClientProvider>
                <StandaloneChatBody
                    studyId={readyState.studyId}
                    studies={readyState.studies}
                    sendToParent={sendToParent}
                    callCommandHandlersRef={callCommandHandlersRef}
                />
            </ClientProvider>
        </div>
    );
};

interface BodyProps {
    studyId: number;
    studies: StandaloneStudiesMap;
    sendToParent: SendToParent;
    callCommandHandlersRef: React.MutableRefObject<CallCommandHandlers | null>;
}

// Inner component required so `useChatClientMethods()` resolves within ChatProvider context.
const StandaloneChatBody = ({ studyId, studies, sendToParent, callCommandHandlersRef }: BodyProps) => {
    const chatFlags = useMemo(() => deriveChatFlags(studyId, studies), [studyId, studies]);
    const studyNameGetter = useMemo(() => buildStudyNameGetter(studies), [studies]);

    return (
        <ChatProvider dashboard="pdash" chatFlags={chatFlags} studyNameGetter={studyNameGetter}>
            <StandaloneChatInner studyId={studyId} sendToParent={sendToParent} callCommandHandlersRef={callCommandHandlersRef} />
        </ChatProvider>
    );
};

interface InnerProps {
    studyId: number;
    sendToParent: SendToParent;
    callCommandHandlersRef: React.MutableRefObject<CallCommandHandlers | null>;
}

const StandaloneChatInner = ({ studyId, sendToParent, callCommandHandlersRef }: InnerProps) => {
    const [category, setCategory] = useState(CATEGORY_TYPES.ALL);
    const [roomId, setRoomId] = useState<string | null>(null);

    const chatClientMethods = useChatClientMethods() as {
        setCurrentStudyId: (studyId: number) => void;
        setCurrentStudyUsersDataLoader: (loader: (userId: number) => Promise<unknown>) => void;
        setRoomOnClick: (roomOnClick: (roomId: string | null) => void) => void;
        setSelectedRoomId: (roomId: string | null) => void;
        answerIncomingVoipCall: (callId: string, audioOnly: boolean) => void;
        rejectIncomingVoipCall: (callId: string) => void;
        hangupActiveVoipCall: () => void;
        setVoipCallMinimized: (value: boolean) => void;
    };

    const voip = useVoip() as { activeCallData?: { id?: string } };
    const activeCallIdRef = useRef<string | null>(null);
    activeCallIdRef.current = voip?.activeCallData?.id ?? null;

    const studyUsersDataLoader = useStudyUsersForChat(studyId);

    useEffect(() => {
        chatClientMethods.setCurrentStudyId(studyId);
    }, [chatClientMethods, studyId]);

    useEffect(() => {
        chatClientMethods.setCurrentStudyUsersDataLoader(studyUsersDataLoader);
    }, [chatClientMethods, studyUsersDataLoader]);

    useEffect(() => {
        chatClientMethods.setRoomOnClick(setRoomId);
    }, [chatClientMethods]);

    useEffect(() => {
        chatClientMethods.setSelectedRoomId(roomId);
    }, [chatClientMethods, roomId]);

    const reportUnreadCount = useCallback(
        (count: number) => {
            sendToParent({ type: 'CHAT_UNREAD_COUNT', payload: { count } });
        },
        [sendToParent]
    );
    useUnreadCountReporter(reportUnreadCount);

    const reportIncomingCall = useCallback(
        (payload: IncomingCallPayload) => {
            sendToParent({ type: 'CHAT_INCOMING_CALL', payload });
        },
        [sendToParent]
    );
    const reportIncomingCallEnded = useCallback(
        (callId: string) => {
            sendToParent({ type: 'CHAT_INCOMING_CALL_ENDED', payload: { callId } });
        },
        [sendToParent]
    );
    useIncomingCallReporter(reportIncomingCall, reportIncomingCallEnded);

    const reportCallState = useCallback(
        (payload: CallStateChangedPayload) => {
            sendToParent({ type: 'CHAT_CALL_STATE_CHANGED', payload });
        },
        [sendToParent]
    );
    useCallStateReporter(reportCallState);

    const reportCallView = useCallback(
        (payload: CallViewChangedPayload) => {
            sendToParent({ type: 'CHAT_CALL_VIEW_CHANGED', payload });
        },
        [sendToParent]
    );
    useCallViewReporter(reportCallView);

    // Register call-command handlers so the top-level bridge can dispatch into matrix methods
    // that only exist inside ChatProvider.
    /* eslint-disable no-param-reassign */
    useLayoutEffect(() => {
        callCommandHandlersRef.current = {
            onAnswerCall: ({ callId, withVideo }) => chatClientMethods.answerIncomingVoipCall(callId, !withVideo),
            onRejectCall: ({ callId }) => chatClientMethods.rejectIncomingVoipCall(callId),
            onHangupCall: ({ callId }) => {
                if (activeCallIdRef.current !== callId) {
                    Sentry.addBreadcrumb({
                        category: 'standalone-chat',
                        level: Sentry.Severity.Info,
                        message: 'Ignored CHAT_HANGUP_CALL: callId mismatch',
                        data: { requested: callId, current: activeCallIdRef.current },
                    });
                    return;
                }
                chatClientMethods.hangupActiveVoipCall();
            },
            onSetCallView: ({ mode }) => {
                if (activeCallIdRef.current === null) {
                    Sentry.addBreadcrumb({
                        category: 'standalone-chat',
                        level: Sentry.Severity.Info,
                        message: 'Ignored CHAT_SET_CALL_VIEW: no active call',
                        data: { mode },
                    });
                    return;
                }
                chatClientMethods.setVoipCallMinimized(mode === CALL_VIEW_MODES.MINIMIZED);
            },
        };
        return () => {
            callCommandHandlersRef.current = null;
        };
    }, [chatClientMethods, callCommandHandlersRef]);
    /* eslint-enable no-param-reassign */

    return (
        <div className={classes.chatWrapper}>
            <Chat
                key={studyId}
                currentStudyId={studyId}
                studyUsersDataLoader={studyUsersDataLoader}
                category={category}
                categoryOnChange={setCategory}
                roomId={roomId}
                roomOnClick={setRoomId}
            />
        </div>
    );
};

export default StandaloneChatPage;
